/**
 * User-global observability hook installer (Issue #174 / T-A1, T-A3, T-A5).
 *
 * Configures USER-scope hooks ONCE on a developer machine so 3-axis records are
 * captured even outside the @aaac/runtime process and in repos that never ran
 * `aaac init`:
 *
 *   • Cursor : ~/.cursor/hooks.json        (session/subagent/tool/edit/shell)
 *   • Claude : ~/.claude/settings.json     (SessionStart/PreToolUse/.../Stop)
 *   • Git    : ~/.aaac/git-hooks/*  + core.hooksPath (--global)
 *
 * All three are merged idempotently and non-destructively: re-running produces
 * byte-identical files, hand-written hooks are preserved, and the global git
 * hook chains to any repo-local hook rather than clobbering it.
 *
 * Pure builders + a thin materialize step (with --dry-run) so the merge logic is
 * unit-testable without touching a real $HOME. Cursor merging reuses
 * governance.ts (mergeCursorHooks / parseCursorHooksFile) — no new pattern.
 *
 * Proposal: docs/proposal/observability-integration.md §ローカルグローバル記録.
 */
import { execFileSync } from "node:child_process";
import { existsSync } from "node:fs";
import { chmod, mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";
import {
  mergeCursorHooks,
  parseCursorHooksFile,
  serializeCursorHooksFile,
  type CursorHookEntry,
} from "./governance.js";

/** The user-global recorder binary the hooks invoke. */
export const GLOBAL_RECORDER_CMD = "aaac-observ-global-record";

// ---------------------------------------------------------------------------
// Cursor (T-A3)
// ---------------------------------------------------------------------------

/**
 * Cursor hook events wired by the global installer.
 *
 * Dedup: re-firing is tolerated at RECORD time — the event_mapping injects a
 * stable key (tool.use_id for tool events, span name + session_id otherwise)
 * so duplicate fires collapse to the same span. The installer keeps the file
 * idempotent by keying merge on the command string (see mergeCursorHooks).
 */
export const GLOBAL_CURSOR_HOOK_EVENTS = [
  "sessionStart",
  "sessionEnd",
  "subagentStart",
  "subagentStop",
  "preToolUse",
  "postToolUse",
  "afterFileEdit",
  "afterShellExecution",
] as const;

export function buildGlobalCursorEntries(recorder = GLOBAL_RECORDER_CMD): CursorHookEntry[] {
  return GLOBAL_CURSOR_HOOK_EVENTS.map((event) => ({
    event,
    command: `${recorder} ${event} --source cursor-hook`,
  }));
}

/** ~/.cursor/hooks.json */
export function cursorHooksPath(home: string): string {
  return join(home, ".cursor", "hooks.json");
}

// ---------------------------------------------------------------------------
// Claude Code (T-A4 wiring)
// ---------------------------------------------------------------------------

export interface ClaudeCommandHook {
  type: "command";
  command: string;
  [key: string]: unknown;
}

export interface ClaudeHookGroup {
  matcher?: string;
  hooks: ClaudeCommandHook[];
  [key: string]: unknown;
}

export interface ClaudeSettings {
  hooks?: Record<string, ClaudeHookGroup[]>;
  [key: string]: unknown;
}

/** Claude Code hook events projected to agent-axis spans (see observability.yaml). */
export const GLOBAL_CLAUDE_HOOK_EVENTS = [
  "SessionStart",
  "PreToolUse",
  "PostToolUse",
  "Stop",
  "SubagentStop",
] as const;

export function buildClaudeHookCommand(event: string, recorder = GLOBAL_RECORDER_CMD): string {
  return `${recorder} ${event} --source claude-hook`;
}

/** ~/.claude/settings.json */
export function claudeSettingsPath(home: string): string {
  return join(home, ".claude", "settings.json");
}

/** Parse settings.json, fail-open to {} on empty/invalid input. */
export function parseClaudeSettings(raw: string | undefined): ClaudeSettings {
  if (!raw || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as ClaudeSettings)
      : {};
  } catch {
    return {};
  }
}

export function serializeClaudeSettings(settings: ClaudeSettings): string {
  return JSON.stringify(settings, null, 2) + "\n";
}

/**
 * Idempotently merge the global recorder into Claude's hooks map.
 *
 * For each event we ensure exactly one command-hook with our command exists;
 * existing user groups (and any other settings keys) are preserved untouched.
 * Re-running is a no-op (the command is already present).
 */
export function mergeClaudeSettings(
  existing: ClaudeSettings,
  recorder = GLOBAL_RECORDER_CMD,
): ClaudeSettings {
  const settings: ClaudeSettings = { ...existing };
  const hooks: Record<string, ClaudeHookGroup[]> = {};
  for (const [event, groups] of Object.entries(existing.hooks ?? {})) {
    hooks[event] = groups.map((g) => ({ ...g, hooks: g.hooks.map((h) => ({ ...h })) }));
  }

  for (const event of GLOBAL_CLAUDE_HOOK_EVENTS) {
    const command = buildClaudeHookCommand(event, recorder);
    const groups = hooks[event] ?? [];
    const already = groups.some((g) => g.hooks.some((h) => h.command === command));
    if (!already) {
      groups.push({ hooks: [{ type: "command", command }] });
    }
    hooks[event] = groups;
  }

  settings.hooks = hooks;
  return settings;
}

// ---------------------------------------------------------------------------
// Git (T-A5)
// ---------------------------------------------------------------------------

/** Git hooks recorded by the global installer (promotion axis). */
export const GLOBAL_GIT_HOOK_NAMES = [
  "pre-commit",
  "commit-msg",
  "post-commit",
  "pre-push",
] as const;

/** ~/.aaac/git-hooks */
export function globalGitHooksDir(home: string): string {
  return join(home, ".aaac", "git-hooks");
}

/**
 * A single global git hook script (identical for every hook name — it derives
 * the hook name from its own basename).
 *
 * Behaviour:
 *   1. record the promotion event via the global recorder (fail-open: `|| true`)
 *   2. chain to the repo-local `.git/hooks/<name>` when it exists & is executable
 *      so per-repo hooks are NEVER clobbered (core.hooksPath would otherwise
 *      shadow them)
 *   3. exit 0
 */
export function buildGlobalGitHookScript(recorder = GLOBAL_RECORDER_CMD): string {
  return [
    "#!/bin/sh",
    "# >>> aaac-observ:global-git-hook (do not edit) >>>",
    "# Records promotion events from ANY repo, then chains to the repo-local hook.",
    'hook_name="$(basename "$0")"',
    `${recorder} "$hook_name" --source git-hook >/dev/null 2>&1 || true`,
    'repo_root="$(git rev-parse --show-toplevel 2>/dev/null)"',
    'if [ -n "$repo_root" ] && [ -x "$repo_root/.git/hooks/$hook_name" ]; then',
    '  exec "$repo_root/.git/hooks/$hook_name" "$@"',
    "fi",
    "exit 0",
    "# <<< aaac-observ:global-git-hook <<<",
    "",
  ].join("\n");
}

// ---------------------------------------------------------------------------
// git config access (injectable for tests)
// ---------------------------------------------------------------------------

export interface GitConfigIO {
  get(key: string): string | undefined;
  set(key: string, value: string): void;
}

/** Default git config IO backed by `git config --global`. */
export const defaultGitConfigIO: GitConfigIO = {
  get(key) {
    try {
      const out = execFileSync("git", ["config", "--global", "--get", key], {
        encoding: "utf8",
        stdio: ["ignore", "pipe", "ignore"],
      });
      const trimmed = out.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    } catch {
      return undefined;
    }
  },
  set(key, value) {
    execFileSync("git", ["config", "--global", key, value], { stdio: "ignore" });
  },
};

// ---------------------------------------------------------------------------
// Materialize
// ---------------------------------------------------------------------------

export type InstallActionKind = "create" | "update" | "unchanged" | "skip";

export interface InstallAction {
  target: "cursor" | "claude" | "git";
  path: string;
  action: InstallActionKind;
  note?: string;
}

export interface InstallGlobalOptions {
  /** $HOME (defaults to process.env.HOME / os.homedir() resolved by the caller). */
  home: string;
  recorder?: string;
  dryRun?: boolean;
  /** Selectively enable targets (all default true). */
  cursor?: boolean;
  claude?: boolean;
  git?: boolean;
  /** Injectable git config IO (defaults to `git config --global`). */
  gitConfig?: GitConfigIO;
}

async function readIfExists(path: string): Promise<string | undefined> {
  if (!existsSync(path)) return undefined;
  return readFile(path, "utf8");
}

/** Plan + (optionally) apply an idempotent merge for a JSON-ish text file. */
async function materializeFile(
  target: InstallAction["target"],
  path: string,
  existing: string | undefined,
  next: string,
  dryRun: boolean,
  mode?: number,
): Promise<InstallAction> {
  let action: InstallActionKind;
  if (existing === undefined) action = "create";
  else if (existing === next) action = "unchanged";
  else action = "update";

  if (!dryRun && action !== "unchanged") {
    await mkdir(dirname(path), { recursive: true });
    await writeFile(path, next, "utf8");
    if (mode !== undefined) await chmod(path, mode);
  }
  return { target, path, action };
}

/**
 * Install (or preview) all user-global observability hooks. Idempotent: a second
 * run with no external changes yields only `unchanged` actions and writes
 * nothing. With `dryRun`, nothing is written at all.
 */
export async function installGlobalHooks(opts: InstallGlobalOptions): Promise<InstallAction[]> {
  const {
    home,
    recorder = GLOBAL_RECORDER_CMD,
    dryRun = false,
    cursor = true,
    claude = true,
    git = true,
    gitConfig = defaultGitConfigIO,
  } = opts;

  const actions: InstallAction[] = [];

  // --- Cursor (~/.cursor/hooks.json) ---
  if (cursor) {
    const path = cursorHooksPath(home);
    const existingRaw = await readIfExists(path);
    const merged = mergeCursorHooks(parseCursorHooksFile(existingRaw), buildGlobalCursorEntries(recorder));
    const next = serializeCursorHooksFile(merged);
    actions.push(await materializeFile("cursor", path, existingRaw, next, dryRun));
  }

  // --- Claude (~/.claude/settings.json) ---
  if (claude) {
    const path = claudeSettingsPath(home);
    const existingRaw = await readIfExists(path);
    const merged = mergeClaudeSettings(parseClaudeSettings(existingRaw), recorder);
    const next = serializeClaudeSettings(merged);
    actions.push(await materializeFile("claude", path, existingRaw, next, dryRun));
  }

  // --- Git (~/.aaac/git-hooks/* + core.hooksPath) ---
  if (git) {
    const hooksDir = globalGitHooksDir(home);
    const script = buildGlobalGitHookScript(recorder);
    for (const name of GLOBAL_GIT_HOOK_NAMES) {
      const path = join(hooksDir, name);
      const existingRaw = await readIfExists(path);
      actions.push(await materializeFile("git", path, existingRaw, script, dryRun, 0o755));
    }

    // core.hooksPath — never clobber a user-set value pointing elsewhere.
    const current = gitConfig.get("core.hooksPath");
    if (current === hooksDir) {
      actions.push({ target: "git", path: "core.hooksPath", action: "unchanged" });
    } else if (current && current.length > 0) {
      actions.push({
        target: "git",
        path: "core.hooksPath",
        action: "skip",
        note: `core.hooksPath already set to ${current}; leave it and source ${hooksDir} from your hooks, or unset to let aaac manage it`,
      });
    } else {
      if (!dryRun) gitConfig.set("core.hooksPath", hooksDir);
      actions.push({ target: "git", path: "core.hooksPath", action: "create" });
    }
  }

  return actions;
}
